<template>
  <b-modal id="createLicenseModal" ref="createLicenseModal" @close="resetModal">
    <template slot="modal-title">{{ $t('createLicense') }}</template>
    <b-form>
      <fieldset :disabled="createLoading">
        <b-form-group id="licenseType-group" :label="$t('licenseModel')" label-for="licenseType">
          <b-form-select
            id="licenseType"
            v-model="licenseModel"
            :options="licenseModelsForCreation"
            autofocus
          >
          </b-form-select>
        </b-form-group>

        <b-form-group id="channel-group" :label="$t('channel')" label-for="channel">
          <b-form-select
            id="channel"
            v-model="channel"
            :options="channelOptions"
          >
            <template slot="first">
              <option :value="null">{{ $t('none')}}</option>
            </template>
          </b-form-select>
        </b-form-group>

        <div v-if="licenseModel === 'PRIVATE'">
          <b-form-group id="private-owner-group" label-for="owner">
            <template v-slot:label>
              {{ $t('owner') }} <span>*</span>
            </template>
            <b-form-input v-if="isActiveProfileSet" type="text" v-model="create.ownerUserId" readonly/>
            <b-input-group v-else>
              <input :placeholder="$t('clickOnUserSearch')" class="form-control" :disabled="true" v-model="create.ownerUserName">
              <b-input-group-prepend>
                <user-search ref="searchUser" @selectedOwnerUser="selectedOwnerUser"/>
                <span class="input-group-text"><b-icon-search @click="$refs['searchUser'].showModal()"/></span>
              </b-input-group-prepend>
            </b-input-group>
          </b-form-group>

          <b-form-group id="private-ean-group" label-for="ean">
            <template v-slot:label>
              {{ $t('product') }} <span>*</span>
            </template>
            <b-form-tags v-model="eanNames" no-outer-focus no-add-on-enter class="mb-2">
              <template v-slot="{ tags }">
                <b-input-group>
                  <input :placeholder="$t('clickOnProductSearch')" class="form-control" :disabled="true">
                  <b-input-group-prepend>
                    <product-search ref="productSearch" @selectedEan="populateProduct"/>
                    <span class="input-group-text"><b-icon-search @click="$refs['productSearch'].showModal()"/></span>
                  </b-input-group-prepend>
                </b-input-group>
                <ul class="mb-0">
                  <li v-for="tag in tags" :key="tag" :title="`Tag: ${tag}`" class="mt-2">
                    <span>
                      <span class="mr-2">{{ tag }}</span>
                      <b-button :disabled="disabled" size="sm" variant="outline-info" @click="removeProduct(tag)">
                        {{ $t('removeProduct') }}
                      </b-button>
                    </span>
                  </li>
                </ul>
              </template>
            </b-form-tags>
          </b-form-group>

          <b-form-group id="private-orderId-group" :label="$t('orderId')" label-for="orderId">
            <b-form-input type="text" v-model="create.orderId"/>
          </b-form-group>

          <b-form-group id="private-validFrom-group" :label="$t('validFrom')" label-for="validFrom">
            <b-form-datepicker size="sm" id="fromDate" v-model="create.validFrom" class="mb-2" reset-button locale="langCode" :label-reset-button="$t('reset')"
                               reset-button-variant="outline-secondary" :placeholder="$t('chooseADate')"/>
          </b-form-group>

          <b-form-group id="private-validTo-group" :label="$t('validTo')" label-for="validTo">
            <b-form-datepicker size="sm" id="validTo" v-model="create.validTo" class="mb-2" reset-button locale="langCode" :label-reset-button="$t('reset')"
                               reset-button-variant="outline-secondary" :placeholder="$t('chooseADate')"/>
          </b-form-group>

          <b-form-group id="private-description-group" :label="$t('description')" label-for="description">
            <b-form-textarea type="text" v-model="create.description"/>
          </b-form-group>
        </div>

        <div v-if="licenseModel === 'SCHOOL'">
          <b-form-group id="school-owner-group" label-for="owner">
            <template v-slot:label>
              {{ $t('owner') }} <span>*</span>
            </template>
            <b-form-input v-if="isActiveProfileSet" type="text" v-model="create.ownerOrganizationNumber" readonly/>
            <b-form-tags v-else v-model="organizationNames" no-outer-focus no-add-on-enter class="mb-2">
              <template v-slot="{ tags }">
                <b-input-group>
                  <input :placeholder="$t('clickOnSchoolSearch')" class="form-control" :disabled="true">
                  <b-input-group-prepend>
                    <organization-search ref="searchSchool"/>
                    <span class="input-group-text"><b-icon-search @click="$refs['searchSchool'].showModal(populateOrganization)"/></span>
                  </b-input-group-prepend>
                </b-input-group>
                <ul class="mb-0">
                  <li v-for="tag in tags" :key="tag" :title="`Tag: ${tag}`" class="mt-2">
                    <span>
                      <span class="mr-2">{{ tag }}</span>
                      <b-button :disabled="disabled" size="sm" variant="outline-info" @click="removeOrganization(tag)">
                        {{ $t('removeOrganization') }}
                      </b-button>
                    </span>
                  </li>
                </ul>
              </template>
            </b-form-tags>
          </b-form-group>
          <b-form-group id="school-ean-group" label-for="ean">
            <template v-slot:label>
              {{ $t('product') }} <span>*</span>
            </template>
            <b-form-tags v-model="eanNames" no-outer-focus no-add-on-enter class="mb-2">
              <template v-slot="{ tags }">
                <b-input-group>
                  <input :placeholder="$t('clickOnProductSearch')" class="form-control" :disabled="true">
                  <b-input-group-prepend>
                    <product-search ref="productSearch" @selectedEan="populateProduct"/>
                    <span class="input-group-text"><b-icon-search @click="$refs['productSearch'].showModal()"/></span>
                  </b-input-group-prepend>
                </b-input-group>
                <ul class="mb-0">
                  <li v-for="tag in tags" :key="tag" :title="`Tag: ${tag}`" class="mt-2">
                    <span>
                      <span class="mr-2">{{ tag }}</span>
                      <b-button :disabled="disabled" size="sm" variant="outline-info" @click="removeProduct(tag)">
                        {{ $t('removeProduct') }}
                      </b-button>
                    </span>
                  </li>
                </ul>
              </template>
            </b-form-tags>
          </b-form-group>

          <b-form-group id="school-orderId-group" :label="$t('orderId')" label-for="orderId">
            <b-form-input type="text" v-model="create.orderId"/>
          </b-form-group>

          <b-form-group id="school-validFrom-group" :label="$t('validFrom')" label-for="validFrom">
            <b-form-datepicker size="sm" id="fromDate" v-model="create.validFrom" class="mb-2" reset-button locale="langCode" :label-reset-button="$t('reset')"
                               reset-button-variant="outline-secondary" :placeholder="$t('chooseADate')"/>
          </b-form-group>

          <b-form-group id="school-validTo-group" :label="$t('validTo')" label-for="validTo">
            <b-form-datepicker size="sm" id="validTo" v-model="create.validTo" class="mb-2" reset-button locale="langCode" :label-reset-button="$t('reset')"
                               reset-button-variant="outline-secondary" :placeholder="$t('chooseADate')"/>
          </b-form-group>

          <b-form-group id="school-description-group" :label="$t('description')" label-for="description">
            <b-form-textarea type="textarea" v-model="create.description"/>
          </b-form-group>
        </div>

        <div v-if="licenseModel === 'INDIVIDUAL' || licenseModel === 'ORGANIZATIONAL'">
          <b-form-group id="individual-school-owner-group" label-for="owner">
            <template v-slot:label>
              {{ $t('owner') }} <span>*</span>
            </template>
            <b-form-input v-if="isActiveProfileSet" type="text" v-model="create.ownerOrganizationNumber" readonly/>
            <b-form-tags v-else v-model="organizationNames" no-outer-focus no-add-on-enter class="mb-2">
              <template v-slot="{ tags }">
                <b-input-group>
                  <input :placeholder="$t('clickOnSchoolSearch')" class="form-control" :disabled="true">
                  <b-input-group-prepend>
                    <organization-search ref="searchSchool"/>
                    <span class="input-group-text"><b-icon-search @click="$refs['searchSchool'].showModal(populateOrganization)"/></span>
                  </b-input-group-prepend>
                </b-input-group>
                <ul class="mb-0">
                  <li v-for="tag in tags" :key="tag" :title="`Tag: ${tag}`" class="mt-2">
                    <span>
                      <span class="mr-2">{{ tag }}</span>
                      <b-button :disabled="disabled" size="sm" variant="outline-info" @click="removeOrganization(tag)">
                        {{ $t('removeOrganization') }}
                      </b-button>
                    </span>
                  </li>
                </ul>
              </template>
            </b-form-tags>
          </b-form-group>

          <b-form-group id="individual-ean-group" label-for="ean">
            <template v-slot:label>
              {{ $t('product') }} <span>*</span>
            </template>
            <b-form-tags v-model="eanNames" no-outer-focus no-add-on-enter class="mb-2">
              <template v-slot="{ tags }">
                <b-input-group>
                  <input :placeholder="$t('clickOnProductSearch')" class="form-control" :disabled="true">
                  <b-input-group-prepend>
                    <product-search ref="productSearch" @selectedEan="populateProduct"/>
                    <span class="input-group-text"><b-icon-search @click="$refs['productSearch'].showModal()"/></span>
                  </b-input-group-prepend>
                </b-input-group>
                <ul class="mb-0">
                  <li v-for="tag in tags" :key="tag" :title="`Tag: ${tag}`" class="mt-2">
                    <span>
                      <span class="mr-2">{{ tag }}</span>
                      <b-button :disabled="disabled" size="sm" variant="outline-info" @click="removeProduct(tag)">
                        {{ $t('removeProduct') }}
                      </b-button>
                    </span>
                  </li>
                </ul>
              </template>
            </b-form-tags>
          </b-form-group>

          <b-form-group id="individual-orderId-group" :label="$t('orderId')" label-for="orderId">
            <b-form-input type="text" v-model="create.orderId"/>
          </b-form-group>

          <b-form-group id="individual-validFrom-group" :label="$t('validFrom')" label-for="validFrom">
            <b-form-datepicker size="sm" id="fromDate" v-model="create.validFrom" class="mb-2" reset-button locale="langCode" :label-reset-button="$t('reset')"
                               reset-button-variant="outline-secondary" :placeholder="$t('chooseADate')"/>
          </b-form-group>

          <b-form-group id="individual-validTo-group" :label="$t('validTo')" label-for="validTo">
            <b-form-datepicker size="sm" id="validTo" v-model="create.validTo" class="mb-2" reset-button locale="langCode" :label-reset-button="$t('reset')"
                               reset-button-variant="outline-secondary" :placeholder="$t('chooseADate')"/>
          </b-form-group>

          <b-form-group id="individual-usageRights-group" :label="$t('usageRights')" label-for="usageRights">
            <b-form-input type="number" v-model="create.usageRights"/>
          </b-form-group>

          <b-form-group id="individual-description-group" :label="$t('description')" label-for="description">
            <b-form-textarea type="text" v-model="create.description"/>
          </b-form-group>
        </div>

        <div v-if="licenseModel === 'OPEN'">
          <b-form-group id="open-ean-group" label-for="ean">
            <template v-slot:label>
              {{ $t('ean') }} <span>*</span>
            </template>
            <b-form-tags v-model="eanNames" no-outer-focus no-add-on-enter class="mb-2">
              <template v-slot="{ tags }">
                <b-input-group>
                  <input :placeholder="$t('clickOnProductSearch')" class="form-control" :disabled="true">
                  <b-input-group-prepend>
                    <product-search ref="productSearch" @selectedEan="populateProduct"/>
                    <span class="input-group-text"><b-icon-search @click="$refs['productSearch'].showModal()"/></span>
                  </b-input-group-prepend>
                </b-input-group>
                <ul class="mb-0">
                  <li v-for="tag in tags" :key="tag" :title="`Tag: ${tag}`" class="mt-2">
                    <span>
                      <span class="mr-2">{{ tag }}</span>
                      <b-button :disabled="disabled" size="sm" variant="outline-info" @click="removeProduct(tag)">
                        {{ $t('removeProduct') }}
                      </b-button>
                    </span>
                  </li>
                </ul>
              </template>
            </b-form-tags>
          </b-form-group>

          <b-form-group id="open-orderId-group" :label="$t('orderId')" label-for="orderId">
            <b-form-input type="text" v-model="create.orderId"/>
          </b-form-group>

          <b-form-group id="open-validFrom-group" :label="$t('validFrom')" label-for="validFrom">
            <b-form-datepicker size="sm" id="fromDate" v-model="create.validFrom" class="mb-2" reset-button locale="langCode" :label-reset-button="$t('reset')"
                               reset-button-variant="outline-secondary" :placeholder="$t('chooseADate')"/>
          </b-form-group>

          <b-form-group id="open-validTo-group" :label="$t('validTo')" label-for="validTo">
            <b-form-datepicker size="sm" id="validTo" v-model="create.validTo" class="mb-2" reset-button locale="langCode" :label-reset-button="$t('reset')"
                               reset-button-variant="outline-secondary" :placeholder="$t('chooseADate')"/>
          </b-form-group>

          <b-form-group id="open-description-group" :label="$t('description')" label-for="description">
            <b-form-textarea type="text" v-model="create.description"/>
          </b-form-group>
        </div>

        <div v-if="licenseModel === 'TRIAL_SCHOOL'">
          <b-form-group id="school-owner-group" label-for="owner">
            <template v-slot:label>
              {{ $t('owner') }} <span>*</span>
            </template>
            <b-form-input v-if="isActiveProfileSet" type="text" v-model="create.ownerOrganizationNumber" readonly/>
            <b-form-tags v-else v-model="organizationNames" no-outer-focus no-add-on-enter class="mb-2">
              <template v-slot="{ tags }">
                <b-input-group>
                  <input :placeholder="$t('clickOnSchoolSearch')" class="form-control" :disabled="true">
                  <b-input-group-prepend>
                    <organization-search ref="searchSchool"/>
                    <span class="input-group-text"><b-icon-search @click="$refs['searchSchool'].showModal(populateOrganization)"/></span>
                  </b-input-group-prepend>
                </b-input-group>
                <ul class="mb-0">
                  <li v-for="tag in tags" :key="tag" :title="`Tag: ${tag}`" class="mt-2">
                    <span>
                      <span class="mr-2">{{ tag }}</span>
                      <b-button :disabled="disabled" size="sm" variant="outline-info" @click="removeOrganization(tag)">
                        {{ $t('removeOrganization') }}
                      </b-button>
                    </span>
                  </li>
                </ul>
              </template>
            </b-form-tags>
          </b-form-group>
          <b-form-group id="school-ean-group" label-for="ean">
            <template v-slot:label>
              {{ $t('product') }} <span>*</span>
            </template>
            <b-form-tags v-model="eanNames" no-outer-focus no-add-on-enter class="mb-2">
              <template v-slot="{ tags }">
                <b-input-group>
                  <input :placeholder="$t('clickOnProductSearch')" class="form-control" :disabled="true">
                  <b-input-group-prepend>
                    <product-search ref="productSearch" @selectedEan="populateProduct"/>
                    <span class="input-group-text"><b-icon-search @click="$refs['productSearch'].showModal()"/></span>
                  </b-input-group-prepend>
                </b-input-group>
                <ul class="mb-0">
                  <li v-for="tag in tags" :key="tag" :title="`Tag: ${tag}`" class="mt-2">
                    <span>
                      <span class="mr-2">{{ tag }}</span>
                      <b-button :disabled="disabled" size="sm" variant="outline-info" @click="removeProduct(tag)">
                        {{ $t('removeProduct') }}
                      </b-button>
                    </span>
                  </li>
                </ul>
              </template>
            </b-form-tags>
          </b-form-group>

          <b-form-group id="school-validFrom-group" :label="$t('validFrom')" label-for="validFrom">
            <b-form-datepicker size="sm" id="fromDate" v-model="create.validFrom" class="mb-2" reset-button locale="langCode" :label-reset-button="$t('reset')"
                               reset-button-variant="outline-secondary" :placeholder="$t('chooseADate')"/>
          </b-form-group>

          <b-form-group id="school-validTo-group" :label="$t('validTo')" label-for="validTo">
            <b-form-datepicker size="sm" id="validTo" v-model="create.validTo" class="mb-2" reset-button locale="langCode" :label-reset-button="$t('reset')"
                               reset-button-variant="outline-secondary" :placeholder="$t('chooseADate')"/>
          </b-form-group>

          <b-form-group id="school-description-group" :label="$t('description')" label-for="description">
            <b-form-textarea type="textarea" v-model="create.description"/>
          </b-form-group>
        </div>

      </fieldset>
    </b-form>

    <div slot="modal-footer" class="col-md-12">
      <b-form>
        <fieldset :disabled="createLoading">
          <b-button variant="primary" class="float-lg-right" @click="createLicense" :disabled="validate">{{ $t('create') }}</b-button>
          <b-button variant="secondary" class="float-lg-left" v-on:click="resetModal">{{ $t('cancel') }}</b-button>
        </fieldset>
      </b-form>
    </div>

  </b-modal>
</template>
<script>

  import OrganizationSearch from '../../components/OrganizationSearch'
  import ProductSearch from '../../components/ProductSearch'
  import { mapGetters } from 'vuex'
  import LicenseService from '../../services/LicenseService'
  import UserSearch from '../../components/UserSearch'
  import BaseComponent from '../../components/BaseComponent'

  export default {
    name: 'CreateLicense',
    extends: BaseComponent,
    components: {
      OrganizationSearch,
      ProductSearch,
      UserSearch
    },
    data: () => ({
      licenseTypeDetails: {},
      disabled: false,
      organizations: new Map(),
      eans: new Map(),
      eanNames: [],
      organizationNames: [],
      organizationNumbers: []
    }),
    computed: {
      ...mapGetters('licenses', ['create', 'createLoading']),
      ...mapGetters('languageStore', ['langCode']),
      ...mapGetters('sessionStorage', ['isActiveProfileSet', 'activeProfile']),

      licenseModel: {
        get() {
          return this.$store.state.licenses.create.licenseModel;
        },
        set(model) {
          this.$store.dispatch('licenses/updateLicenseModel', model)
        },
      },

      channel: {
        get() {
          return this.$store.state.licenses.create.channel;
        },
        set(model) {
          this.$store.dispatch('licenses/updateChannel', model)
        },
      },

      channelOptions() {
        return ['SKOLON', 'AVT']
      },

      licenseModelsForCreation () {
        if (this.isActiveProfileSet === false) {
          return [
            { value: 'PRIVATE', text: this.$t('licenseModels.PRIVATE') },
            { value: 'SCHOOL', text: this.$t('licenseModels.SCHOOL') },
            { value: 'INDIVIDUAL', text: this.$t('licenseModels.INDIVIDUAL') },
            { value: 'OPEN', text: this.$t('licenseModels.OPEN') },
            { value: 'TRIAL_SCHOOL', text: this.$t('licenseModels.TRIAL_SCHOOL') },
            { value: 'ORGANIZATIONAL', text: this.$t('licenseModels.ORGANIZATIONAL') },
          ]
        }
        if (this.isUserAsActiveProfile(this.activeProfile)) {
          return [
            { value: 'PRIVATE', text: this.$t('licenseModels.PRIVATE') }
          ]
        }
        if (this.isOrganizationAsActiveProfile(this.activeProfile)) {
          return [
            { value: 'SCHOOL', text: this.$t('licenseModels.SCHOOL') },
            { value: 'INDIVIDUAL', text: this.$t('licenseModels.INDIVIDUAL') },
            { value: 'TRIAL_SCHOOL', text: this.$t('licenseModels.TRIAL_SCHOOL') },
            { value: 'ORGANIZATIONAL', text: this.$t('licenseModels.ORGANIZATIONAL') },
          ]
        }
        return []
      },
      validate () {
        if (this.licenseModel === 'PRIVATE') {
          if (this.isActiveProfileSet) {
            return (this.eanNames.length === 0)
          } else {
            return (this.eanNames.length === 0 || !this.create.ownerUserName)
          }
        }
        if (this.licenseModel === 'SCHOOL'
          || this.licenseModel === 'INDIVIDUAL'
          || this.licenseModel === 'TRIAL_SCHOOL'
          || this.licenseModel === 'ORGANIZATIONAL'
        ) {
          if (this.isActiveProfileSet) {
            return (this.eanNames.length === 0)
          } else {
            return (this.eanNames.length === 0 || this.organizationNames.length === 0)
          }
        }
        if (this.licenseModel === 'OPEN') {
          return (this.eanNames.length === 0)
        }
        return true
      }
    },
    methods: {
      resetModal () {
        this.$store.dispatch('licenses/resetCreateLicense')
        this.$refs['createLicenseModal'].hide()
        this.organizations = new Map()
        this.eans = new Map()
        this.eanNames = []
        this.organizationNames = []
        this.organizationNumbers = []
      },
      populateOrganization (organization) {
        this.organizations.set(organization.name, organization.organizationNumber)
        this.organizationNames = [...this.organizations.keys()]
      },
      removeOrganization (organization) {
        this.organizations.delete(organization)
        this.organizationNames = this.organizationNames.filter(item => item !== organization)
      },
      populateProduct (ean) {
        this.eans.set(ean.name, ean.ean)
        this.eanNames = [...this.eans.keys()]
      },
      removeProduct (ean) {
        this.eans.delete(ean)
        this.eanNames = this.eanNames.filter(item => item !== ean)
      },
      selectedOwnerUser (user) {
        this.$store.dispatch('licenses/updateUser', user)
      },
      createLicense () {
        let that = this
        LicenseService.bulkCreateLicenses(Array.from(this.organizations.values()), Array.from(this.eans.values()), this.create)
          .then(() => {
            that.$root.$emit('bv::hide::createLicenseModal')
            that.resetModal()
            this.showSuccessToast()
            this.$store.dispatch('licenses/getLicenses')
          })
      },
      showSuccessToast () {
        this.$bvToast.toast(this.$t('licenseCreatedSuccess'), {
          title: this.$t('success'),
          toaster: 'b-toaster-top-center',
          variant: 'success',
          autoHideDelay: 10000
        })
      },
    },
  }
</script>
